import React, { useState, useEffect } from "react";

// ── BADGE helpers ──
const AiBadge = () => (
    <span className="source-badge badge-ai">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="6" fill="#7c3aed" />
            <circle cx="12" cy="12" r="5" stroke="#fff" strokeWidth="2.5" />
        </svg>
        AI
    </span>
);

const SfBadge = () => (
    <span className="source-badge badge-sf">
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none">
            <rect width="24" height="24" rx="6" fill="#00a1e0" />
            <path d="M6 12a6 6 0 1 1 12 0" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
        Salesforce
    </span>
);

const EmailBadge = () => <span className="source-badge badge-email">From Email</span>;
const MissingBadge = () => <span className="source-badge badge-missing">Missing</span>;
const YouBadge = () => <span className="source-badge badge-you">You</span>;

const GetFieldBadge = ({ value, source }) => {
    if (!value && value !== true && value !== false) return <MissingBadge />;
    if (source === "user") return <YouBadge />;
    if (source === "ai") return <AiBadge />;
    if (source === "sf") return <SfBadge />;
    if (source === "email") return <EmailBadge />;
    return null;
};

const CaseForm = ({ fields, handleFieldChange, filledCount, missingCount, dynamicMissing, onSubmit, creatingCase, sfContext, policies, fieldSources, attachments, onToggleAttachment }) => {
    // handleSubmit now always proceeds even with missing fields
    console.log("checking attachments", attachments)
    const handleSubmit = () => {
        onSubmit();
    };
    console.log("checking value of fields", fields)
    return (
        <div className="case-form-wrapper">

            {/* Intent Box */}
            <div className="intent-box">
                <span className="intent-badge">{fields.caseType}</span>
                <p className="intent-desc">
                    {fields.description}
                </p>
            </div>

            {/* Case Fields Header */}
            <div className="case-fields-header">
                <span className="case-fields-title">Case Fields</span>
                <div className="case-fields-counts">
                    <span className="count-filled">{filledCount} filled</span>
                    <span className="count-missing">{missingCount} missing</span>
                </div>
            </div>

            {/* ── Scrollable Fields Area ── */}
            <div className="fields-scroll-area">
                <div className="form-field-group">
                    <label className="form-label">
                        Subject <span className="required-star">*</span>
                        <GetFieldBadge value={fields.subject} source={fieldSources.subject} />
                    </label>
                    <input className="form-input" type="text" value={fields.subject}
                        onChange={(e) => handleFieldChange("subject", e.target.value)} />
                </div>

                {/* ── Contact ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Contact <span className="required-star">*</span>
                        <GetFieldBadge value={fields.contactId} source={fieldSources.contactId} />
                    </label>
                    {sfContext.contacts && sfContext.contacts.length > 0 ? (
                        <select
                            className={`form-select${!fields.contactId ? " input-missing" : ""}`}
                            value={fields.contactId}
                            onChange={(e) => handleFieldChange("contactId", e.target.value)}
                        >
                            <option value="">Select contact...</option>
                            {sfContext.contacts.map((c) => (
                                <option key={c.contactId} value={c.contactId}>
                                    {c.name}
                                </option>
                            ))}
                            {/* Fallback if value is a name from email/AI instead of ID */}
                            {fields.contactId && !sfContext.contacts.some(c => c.contactId === fields.contactId) && (
                                <option value={fields.contactId}>{fields.contactId} (Extracted)</option>
                            )}
                        </select>
                    ) : (
                        <input
                            className={`form-input${!fields.contactId ? " input-missing" : ""}`}
                            type="text"
                            placeholder="Enter contact name..."
                            value={fields.contactId}
                            onChange={(e) => handleFieldChange("contactId", e.target.value)}
                        />
                    )}
                </div>

                {/* ── Related Policy ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Related Policy <span className="required-star">*</span>
                        <GetFieldBadge value={fields.policyId} source={fieldSources.policyId} />
                    </label>
                    {policies && policies.length > 0 ? (
                        <select
                            className={`form-select${!fields.policyId ? " input-missing" : ""}`}
                            value={fields.policyId}
                            onChange={(e) => handleFieldChange("policyId", e.target.value)}
                        >
                            <option value="">Select policy...</option>
                            {policies.map((p) => (
                                <option key={p.policyId || p.id} value={p.policyId || p.id}>
                                    {p.policyNumber} ({p.type || p.lineOfBusiness || "N/A"})
                                </option>
                            ))}
                            {/* Fallback for AI extracted policy number (not ID) */}
                            {fields.policyId && !policies.some(p => (p.policyId || p.id) === fields.policyId) && (
                                <option value={fields.policyId}>{fields.policyId} (Extracted)</option>
                            )}
                        </select>
                    ) : (
                        <input
                            className={`form-input${!fields.policyId ? " input-missing" : ""}`}
                            type="text"
                            placeholder="Enter policy number..."
                            value={fields.policyId}
                            onChange={(e) => handleFieldChange("policyId", e.target.value)}
                        />
                    )}
                </div>

                {/* ── Case Type ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Case Type <span className="required-star">*</span>
                        <GetFieldBadge value={fields.caseType} source={fieldSources.caseType} />
                    </label>
                    <select
                        className={`form-select${!fields.caseType ? " input-missing" : ""}`}
                        value={fields.caseType}
                        onChange={(e) => handleFieldChange("caseType", e.target.value)}>
                        <option value="">Select case type...</option>
                        <option value="Claim Inquiry">Claim Inquiry</option>
                        <option value="Policy Change">Policy Change</option>
                        <option value="Billing">Billing</option>
                        <option value="Quote Request">Quote Request</option>
                        <option value="COI Request">COI Request</option>
                        <option value="Mortgage Update">Mortgage Update</option>
                        <option value="Cancellation">Cancellation</option>
                        <option value="General Enquiry">General Enquiry</option>
                    </select>
                </div>

                {/* ── Priority ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Priority <span className="required-star">*</span>
                        <GetFieldBadge value={fields.priority} source={fieldSources.priority} />
                    </label>
                    <select
                        className={`form-select${!fields.priority ? " input-missing" : ""}`}
                        value={fields.priority}
                        onChange={(e) => handleFieldChange("priority", e.target.value)}>
                        <option value="">Select priority...</option>
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Urgent">Urgent</option>
                    </select>
                </div>

                {/* ── Description ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Description <span className="required-star">*</span>
                        <GetFieldBadge value={fields.description} source={fieldSources.description} />
                    </label>
                    <textarea
                        className={`form-textarea${!fields.description ? " input-missing" : ""}`}
                        value={fields.description}
                        onChange={(e) => handleFieldChange("description", e.target.value)}
                        rows={4}
                    />
                </div>

                {/* ── Date of Loss (MISSING) ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Date of Loss <span className="required-star">*</span>
                        <GetFieldBadge value={fields.dateOfLoss} source={fieldSources.dateOfLoss} />
                    </label>
                    <input
                        className={`form-input${!fields.dateOfLoss ? " input-missing" : ""}`}
                        type="date"
                        value={fields.dateOfLoss}
                        onChange={(e) => handleFieldChange("dateOfLoss", e.target.value)}
                    />
                </div>

                {/* ── Location of Incident ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Location of Incident
                        <GetFieldBadge value={fields.locationOfIncident} source={fieldSources.locationOfIncident} />
                    </label>
                    <input className="form-input" type="text" value={fields.locationOfIncident}
                        onChange={(e) => handleFieldChange("locationOfIncident", e.target.value)} />
                </div>

                {/* ── Other Party Involved ── */}
                <div className="form-field-group">
                    <label className="form-label">Other Party Involved <span className="required-star">*</span> <EmailBadge /></label>
                    <label className="checkbox-label">
                        <input type="checkbox" className="form-checkbox"
                            checked={fields.otherPartyInvolved}
                            onChange={(e) => handleFieldChange("otherPartyInvolved", e.target.checked)} />
                        <span>Yes</span>
                    </label>
                </div>

                {/* ── Other Party Insurance Info (MISSING) ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Other Party's Insurance Info
                        {!fields.otherPartyInsuranceInfo ? <MissingBadge /> : <EmailBadge />}
                    </label>
                    <input
                        className={`form-input${!fields.otherPartyInsuranceInfo ? " input-missing" : ""}`}
                        type="text"
                        placeholder="Enter other party's insurance info..."
                        value={fields.otherPartyInsuranceInfo}
                        onChange={(e) => handleFieldChange("otherPartyInsuranceInfo", e.target.value)}
                    />
                </div>

                {/* ── Police Report Filed (MISSING) ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Police Report Filed <span className="required-star">*</span>
                        {!fields.policeReportFiled ? <MissingBadge /> : <AiBadge />}
                    </label>
                    <select
                        className={`form-select${!fields.policeReportFiled ? " input-missing" : ""}`}
                        value={fields.policeReportFiled}
                        onChange={(e) => handleFieldChange("policeReportFiled", e.target.value)}>
                        <option value="">Select police report filed...</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="Unknown">Unknown</option>
                    </select>
                </div>

                {/* ── Injuries Reported ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Injuries Reported <span className="required-star">*</span>
                        {!fields.injuriesReported ? <MissingBadge /> : <AiBadge />}
                    </label>
                    <select
                        className={`form-select${!fields.injuriesReported ? " input-missing" : ""}`}
                        value={fields.injuriesReported}
                        onChange={(e) => handleFieldChange("injuriesReported", e.target.value)}>
                        <option value="">Select injuries reported...</option>
                        <option value="None">None</option>
                        <option value="Minor">Minor</option>
                        <option value="Serious">Serious</option>
                    </select>
                </div>

                {/* ── Fault Status ── */}
                <div className="form-field-group">
                    <label className="form-label">
                        Fault Status
                        {!fields.faultStatus ? <MissingBadge /> : <AiBadge />}
                    </label>
                    <select
                        className={`form-select${!fields.faultStatus ? " input-missing" : ""}`}
                        value={fields.faultStatus}
                        onChange={(e) => handleFieldChange("faultStatus", e.target.value)}>
                        <option value="">Select fault status...</option>
                        <option value="At Fault">At Fault</option>
                        <option value="Not At Fault">Not At Fault</option>
                        <option value="Shared Fault">Shared Fault</option>
                        <option value="Unknown">Unknown</option>
                    </select>
                </div>

                {/* ── Attachments Section ── */}

                {attachments && attachments.length > 0 && (
                    <div className="attachments-section">
                        <div className="attachments-header">
                            <span className="attachments-title">Email Attachments</span>
                            <span className="attachments-count">{attachments.filter(a => a.selected).length} selected</span>
                        </div>
                        <div className="attachments-list">
                            {attachments.map((att) => (
                                <div
                                    key={att.id}
                                    className={`attachment-item ${att.selected ? "selected" : ""}`}
                                    onClick={() => onToggleAttachment(att.id)}
                                >
                                    <input
                                        type="checkbox"
                                        className="attachment-checkbox"
                                        checked={att.selected}
                                        readOnly
                                    />
                                    <div className="attachment-info">
                                        <span className="attachment-name" title={att.name}>{att.name}</span>
                                        <span className="attachment-meta">
                                            {(att.size / 1024).toFixed(1)} KB • {att.contentType}
                                        </span>
                                    </div>
                                    <div className="attachment-icon">
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M12 2V12M12 12L8 8M12 12L16 8M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H12" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>{/* end fields-scroll-area */}

            {/* ── Missing Fields Warning — FIXED BOTTOM ── */}
            {missingCount > 0 && (
                <div className="missing-warning-box">
                    <div className="missing-warning-title">
                        <span>⚠️</span>
                        <strong>{missingCount} Required Fields Missing</strong>
                    </div>
                    <p className="missing-warning-desc">
                        The AI will draft a follow-up email to ask the client for:{" "}
                        <span className="missing-warning-fields">{dynamicMissing.join(", ")}</span>
                    </p>
                </div>
            )}

            {/* ── Submit Button ── */}
            <button
                className={`submit-case-btn ${creatingCase ? "btn-disabled" : ""}`}
                onClick={handleSubmit}
                disabled={creatingCase}
            >
                {creatingCase ? (
                    <>
                        <div className="submit-btn-icon-wrapper">
                            <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                        </div>
                        <span className="btn-text">Creating Case...</span>
                    </>
                ) : (
                    <>
                        <div className="submit-btn-icon-wrapper">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="2.5" />
                                <path d="M8 12L11 15L16 9" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <span className="btn-text">Create Case &amp; Generate Follow-up Email!</span>
                    </>
                )}
            </button>


        </div>
    );
};

export default CaseForm;
